import { api } from '../lib/api';
import type {
  AcceptInviteResponse,
  BillingPortalResponse,
  CreateOrgResponse,
  OrgDetail,
  OrgSummary,
  Role,
  SubscriptionStatus,
} from '../lib/types';

export async function fetchOrgs(): Promise<OrgSummary[]> {
  const response = await api<{ orgs: OrgSummary[] }>('/v1/orgs');
  return response.orgs;
}

export function fetchOrg(orgId: string): Promise<OrgDetail> {
  return api<OrgDetail>(`/v1/orgs/${orgId}`);
}

export function createOrg(name: string): Promise<CreateOrgResponse> {
  return api<CreateOrgResponse>('/v1/orgs', { method: 'POST', body: { name } });
}

export function inviteMember(orgId: string, email: string, role: Role): Promise<unknown> {
  return api<unknown>(`/v1/orgs/${orgId}/invites`, { method: 'POST', body: { email, role } });
}

export function acceptInvite(token: string): Promise<AcceptInviteResponse> {
  return api<AcceptInviteResponse>(`/v1/invites/${token}/accept`, { method: 'POST' });
}

/** `role: null` removes the member. */
export function setMemberRole(
  orgId: string,
  userId: string,
  role: Role | null,
): Promise<{ ok: boolean; seats: number }> {
  return api<{ ok: boolean; seats: number }>(`/v1/orgs/${orgId}/members/${userId}`, {
    method: 'PATCH',
    body: { role },
  });
}

export function billingPortal(orgId: string): Promise<BillingPortalResponse> {
  return api<BillingPortalResponse>(`/v1/orgs/${orgId}/billing-portal`);
}

// ---------------------------------------------------------------------------
// Role and subscription helpers — the whole role-aware UI reads from these.
// ---------------------------------------------------------------------------

export const ROLE_ORDER: Record<Role, number> = { user: 0, admin: 1, owner: 2 };

export function atLeast(role: Role | undefined, minimum: Role): boolean {
  return role !== undefined && ROLE_ORDER[role] >= ROLE_ORDER[minimum];
}

export function roleLabel(role: Role): string {
  switch (role) {
    case 'owner':
      return 'Owner';
    case 'admin':
      return 'Admin';
    default:
      return 'Member';
  }
}

export function roleDescription(role: Role): string {
  switch (role) {
    case 'owner':
      return 'Everything an admin can do, plus billing and the subscription.';
    case 'admin':
      return 'Sees every recording in the org, and manages members.';
    default:
      return 'Records to their own library and the team workspace.';
  }
}

export interface SubscriptionNotice {
  tone: 'caution' | 'danger' | 'info';
  title: string;
  body: string;
  /** Owners get an action; everyone else gets the explanation only. */
  ownerAction?: string;
}

/**
 * Trial and past-due states are banners, never walls. A team that forgot to
 * add a card should still be able to watch everything they already recorded.
 */
export function subscriptionNotice(
  status: SubscriptionStatus,
  trialEndsAt: string | null,
): SubscriptionNotice | null {
  if (status === 'active') return null;

  if (status === 'trialing') {
    const ends = trialEndsAt ? new Date(trialEndsAt).getTime() : null;
    if (!ends) return null;
    const daysLeft = Math.ceil((ends - Date.now()) / 86_400_000);
    if (daysLeft > 5) return null;
    return {
      tone: 'caution',
      title: daysLeft <= 0 ? 'Your trial has ended' : `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left on your trial`,
      body:
        daysLeft <= 0
          ? 'Add a payment method to keep recording to this workspace. Nothing you have already recorded goes away.'
          : 'Add a payment method whenever you like — everything keeps working until then.',
      ownerAction: 'Set up billing',
    };
  }

  if (status === 'past_due') {
    return {
      tone: 'danger',
      title: 'Payment failed',
      body:
        'New recordings to this workspace are paused until the subscription is current. Everything already here stays watchable.',
      ownerAction: 'Update payment method',
    };
  }

  return {
    tone: 'danger',
    title: 'Subscription canceled',
    body:
      'This workspace is read-only. Existing recordings and share links keep working; new ones need an active subscription.',
    ownerAction: 'Restart the subscription',
  };
}

/** Can anybody record into this org right now? Mirrors the API's rule. */
export function orgAcceptsRecordings(status: SubscriptionStatus): boolean {
  return status === 'trialing' || status === 'active';
}
